import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { format } from "date-fns";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

type Run = {
  id: string;
  task_id: string;
  model: string;
  score: number | null;
  cost_usd: number | null;
  latency_ms: number | null;
  triggered_by: string | null;
  triggered_by_user: string | null;
  judge_model: string | null;
  created_at: string;
  research_eval_tasks: { name: string; task_kind: string; rubric: any } | null;
};

export default function ResearchEvalRuns() {
  const { data: canManage, isLoading: capLoading } = useResearchCapability("manage");
  const [taskFilter, setTaskFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [trigFilter, setTrigFilter] = useState("");
  const [minScore, setMinScore] = useState("");

  const { data: runs, isLoading } = useQuery({
    queryKey: ["research_eval_runs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_eval_runs")
        .select("*, research_eval_tasks(name,task_kind,rubric)")
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return data as unknown as Run[];
    },
    enabled: !!canManage,
  });

  const tasks = useMemo(() => [...new Set((runs ?? []).map((r) => r.research_eval_tasks?.name).filter(Boolean))].sort() as string[], [runs]);
  const models = useMemo(() => [...new Set((runs ?? []).map((r) => r.model))].sort(), [runs]);
  const triggers = useMemo(() => [...new Set((runs ?? []).map((r) => r.triggered_by).filter(Boolean))].sort() as string[], [runs]);

  const filtered = useMemo(() => {
    const min = minScore ? Number(minScore) : null;
    return (runs ?? []).filter((r) => {
      if (taskFilter && r.research_eval_tasks?.name !== taskFilter) return false;
      if (modelFilter && r.model !== modelFilter) return false;
      if (trigFilter && r.triggered_by !== trigFilter) return false;
      if (min != null && (r.score ?? 0) < min) return false;
      return true;
    });
  }, [runs, taskFilter, modelFilter, trigFilter, minScore]);

  const avgByTask = useMemo(() => {
    const m = new Map<string, { name: string; sum: number; n: number; pass: number; threshold: number }>();
    for (const r of filtered) {
      const name = r.research_eval_tasks?.name ?? "—";
      const threshold = Number(r.research_eval_tasks?.rubric?.pass_threshold ?? 0.7);
      const cur = m.get(name) ?? { name, sum: 0, n: 0, pass: 0, threshold };
      cur.sum += Number(r.score ?? 0);
      cur.n += 1;
      if (Number(r.score ?? 0) >= threshold) cur.pass += 1;
      m.set(name, cur);
    }
    return [...m.values()].map((v) => ({
      name: v.name,
      avg: v.n ? v.sum / v.n : 0,
      passRate: v.n ? v.pass / v.n : 0,
      runs: v.n,
      threshold: v.threshold,
    })).sort((a, b) => b.runs - a.runs);
  }, [filtered]);

  const weeklyByModel = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - 90);
    const buckets = new Map<string, Record<string, any>>();
    for (const r of filtered) {
      const d = new Date(r.created_at);
      if (d < since) continue;
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      monday.setHours(0, 0, 0, 0);
      const key = monday.toISOString().slice(0, 10);
      const row = buckets.get(key) ?? { week: key };
      const m = r.model;
      row[`__sum_${m}`] = (row[`__sum_${m}`] ?? 0) + Number(r.score ?? 0);
      row[`__n_${m}`] = (row[`__n_${m}`] ?? 0) + 1;
      buckets.set(key, row);
    }
    return [...buckets.values()].map((row) => {
      const out: any = { week: row.week };
      for (const k of Object.keys(row)) {
        if (k.startsWith("__sum_")) {
          const m = k.slice(6);
          out[m] = row[`__n_${m}`] ? row[`__sum_${m}`] / row[`__n_${m}`] : 0;
        }
      }
      return out;
    }).sort((a, b) => a.week.localeCompare(b.week));
  }, [filtered]);

  const modelKeys = useMemo(() => [...new Set(filtered.map((r) => r.model))], [filtered]);

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Eval runs</h2>
          <p className="text-sm text-muted-foreground">{runs?.length ?? 0} runs</p>
        </div>
        <Button asChild variant="outline"><Link to="/partner/research/evals">← Tasks</Link></Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)}>
          <option value="">All tasks</option>
          {tasks.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
          <option value="">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={trigFilter} onChange={(e) => setTrigFilter(e.target.value)}>
          <option value="">All triggers</option>
          {triggers.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Input
          type="number" min={0} max={1} step={0.05}
          placeholder="Min score"
          value={minScore}
          onChange={(e) => setMinScore(e.target.value)}
          className="w-32"
        />
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} shown</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3 text-sm">Average score by task</h3>
          {avgByTask.length === 0 ? <p className="text-sm text-muted-foreground">No runs.</p> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Avg</TableHead>
                  <TableHead>Pass rate</TableHead>
                  <TableHead>Runs</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {avgByTask.map((r) => (
                    <TableRow key={r.name}>
                      <TableCell className="text-xs">{r.name}</TableCell>
                      <TableCell>{r.avg.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={r.passRate >= 0.7 ? "text-emerald-600" : "text-amber-600"}>
                          {(r.passRate * 100).toFixed(0)}%
                        </span>
                        <span className="text-xs text-muted-foreground"> (≥{r.threshold})</span>
                      </TableCell>
                      <TableCell>{r.runs}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3 text-sm">Avg score by model (weekly, last 90d)</h3>
          {weeklyByModel.length === 0 ? <p className="text-sm text-muted-foreground">No runs in window.</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={weeklyByModel}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {modelKeys.map((m, i) => (
                  <Line key={m} type="monotone" dataKey={m} stroke={`hsl(${(i * 67) % 360} 70% 45%)`} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Loading runs…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Triggered by</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice(0, 500).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{r.research_eval_tasks?.name ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.model}</TableCell>
                    <TableCell>{r.score != null ? Number(r.score).toFixed(2) : "—"}</TableCell>
                    <TableCell>{r.cost_usd != null ? `$${Number(r.cost_usd).toFixed(4)}` : "—"}</TableCell>
                    <TableCell>{r.latency_ms != null ? `${r.latency_ms}ms` : "—"}</TableCell>
                    <TableCell><Badge variant="outline">{r.triggered_by ?? "—"}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(r.created_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {filtered.length > 500 && (
        <p className="text-xs text-muted-foreground">Showing first 500 of {filtered.length} runs.</p>
      )}
    </div>
  );
}
